import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { GetUserIfLoggedIn, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Role, UserMembership } from "data/models";
import { updateUserRoleOrgMembership } from "data/mutations-common";
import { resolveIdentityId } from "utils/identity";

interface TransferOwnershipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  organisationName: string;
  members: UserMembership[];
}

export const TransferOwnershipDialog = ({
  open,
  onOpenChange,
  organisationId,
  organisationName,
  members,
}: TransferOwnershipDialogProps) => {
  const [selectedNewOwner, setSelectedNewOwner] = useState("");
  const [confirmationName, setConfirmationName] = useState("");

  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();
  const { enqueueMutationFailed, enqueueInfoSnackbar } = useCustomSnackbars();

  const transferOwnershipMutation = useMutation({
    mutationFn: (memberUserId: string) =>
      updateUserRoleOrgMembership(Role.OWNER, memberUserId, organisationId),
    onSuccess: () => {
      const newOwner = members.find(
        (member) => member.userMemberId === selectedNewOwner,
      );
      const newOwnerName = newOwner?.userFullName || newOwner?.userEmail;

      enqueueInfoSnackbar(
        `Ownership of ${organisationName} has been successfully transferred to ${newOwnerName}. Your role has been updated to Admin.`,
        "success",
      );

      reset();
      onOpenChange(false);

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.organisationInvitations, identityId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.permissions, identityId],
      });
    },
    onError: (error: Error) => {
      enqueueMutationFailed(error.message);
    },
  });

  const reset = () => {
    setSelectedNewOwner("");
    setConfirmationName("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transfer Ownership</DialogTitle>
          <DialogDescription>
            This is a permanent and irreversible action. The new owner will have
            full, unrestricted control over the {organisationName} organisation.
            Upon successful transfer your role will be immediately changed to
            admin.
          </DialogDescription>
        </DialogHeader>
        <FieldSet>
          <Field>
            <FieldLabel>New Owner</FieldLabel>
            <Select
              value={selectedNewOwner}
              onValueChange={(value) => setSelectedNewOwner(value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {members
                  .filter((member) => {
                    const user = GetUserIfLoggedIn(identity);
                    return (
                      user &&
                      member.userMemberId !== user.cognitoSub &&
                      member.status === "Accepted"
                    );
                  })
                  .map((member) => (
                    <SelectItem
                      key={member.userMemberId}
                      value={member.userMemberId}
                    >
                      {member.userFullName ||
                        `${member.userFirstName} ${member.userLastName}`}{" "}
                      ({member.userEmail || member.organisationEmail})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="confirmation-name">
              Type <b>{organisationName}</b> to confirm
            </FieldLabel>
            <Input
              id="confirmation-name"
              value={confirmationName}
              onChange={(e) => setConfirmationName(e.target.value)}
              placeholder="Organisation name"
            />
          </Field>
        </FieldSet>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={
              !selectedNewOwner ||
              confirmationName.trim() !== organisationName.trim() ||
              transferOwnershipMutation.isPending
            }
            onClick={() => {
              if (selectedNewOwner) {
                transferOwnershipMutation.mutate(selectedNewOwner);
              }
            }}
          >
            {transferOwnershipMutation.isPending ? (
              <>
                <Spinner />
                Transferring...
              </>
            ) : (
              "Transfer Ownership"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
